// Single source of truth for which playground models accept a reasoning
// parameter, what effort levels they accept, and how to forward the value
// to the provider's API. Used by the UI (to show/hide the control and
// populate options) and by the backend (to validate before forwarding).
//
// To add a new reasoning-capable model: append it to MODEL_REASONING below.
// `paramName` controls the wire-format key sent to the provider:
//   - "reasoning_effort": forwards `{ reasoning_effort: <effort> }`.
//   - "reasoning_format": forwards `{ reasoning_format: "parsed" }`
//     (treated as on/off, the chosen effort is ignored for the wire param
//     but still drives the UI for consistency).

const ALL_EFFORTS = ['minimal', 'low', 'medium', 'high'];

// OpenAI removed `minimal` from the GPT-5 reasoning_effort enum (supported
// values are now none/low/medium/high/xhigh). Keep "minimal" as a UI-facing
// option for parity, but remap it to "low" when forwarding to OpenAI.
const GPT5_WIRE_EFFORT_MAP = { minimal: 'low' };

const MODEL_REASONING = {
  // ---- OpenAI ----
  // gpt-5 family (reasoning) – minimal/low/medium/high (minimal sent as low)
  'gpt-5': { paramName: 'reasoning_effort', allowedEfforts: ALL_EFFORTS, wireEffortMap: GPT5_WIRE_EFFORT_MAP },
  'gpt-5-mini': { paramName: 'reasoning_effort', allowedEfforts: ALL_EFFORTS, wireEffortMap: GPT5_WIRE_EFFORT_MAP },
  'gpt-5-nano': { paramName: 'reasoning_effort', allowedEfforts: ALL_EFFORTS, wireEffortMap: GPT5_WIRE_EFFORT_MAP },
  'gpt-5.4': { paramName: 'reasoning_effort', allowedEfforts: ALL_EFFORTS, wireEffortMap: GPT5_WIRE_EFFORT_MAP },
  'gpt-5.4-mini': { paramName: 'reasoning_effort', allowedEfforts: ALL_EFFORTS, wireEffortMap: GPT5_WIRE_EFFORT_MAP },
  'gpt-5.4-nano': { paramName: 'reasoning_effort', allowedEfforts: ALL_EFFORTS, wireEffortMap: GPT5_WIRE_EFFORT_MAP },
  // gpt-5.x-chat-latest are NON-reasoning chat variants — intentionally absent.
  // o-series reasoning models – low/medium/high (no "minimal").
  o1: { paramName: 'reasoning_effort', allowedEfforts: ['low', 'medium', 'high'] },
  o3: { paramName: 'reasoning_effort', allowedEfforts: ['low', 'medium', 'high'] },
  'o4-mini': { paramName: 'reasoning_effort', allowedEfforts: ['low', 'medium', 'high'] },

  // ---- XAI ----
  // grok-4 reasons internally and rejects reasoning_effort, so it's omitted.
  'grok-3-mini': { paramName: 'reasoning_effort', allowedEfforts: ['low', 'high'] },
  'grok-3-mini-fast': { paramName: 'reasoning_effort', allowedEfforts: ['low', 'high'] },

  // ---- Groq ----
  'openai/gpt-oss-20b': { paramName: 'reasoning_effort', allowedEfforts: ['low', 'medium', 'high'] },
  'openai/gpt-oss-120b': { paramName: 'reasoning_effort', allowedEfforts: ['low', 'medium', 'high'] },
  'qwen/qwen3-32b': { paramName: 'reasoning_effort', allowedEfforts: ['low', 'medium', 'high'] },
  'groq/compound': { paramName: 'reasoning_effort', allowedEfforts: ['low', 'medium', 'high'] },
  'groq/compound-mini': { paramName: 'reasoning_effort', allowedEfforts: ['low', 'medium', 'high'] },
  // deepseek-r1-distill / kimi-k2 only expose reasoning_format (no effort knob);
  // expose a low/high toggle that maps to format=parsed on the wire.
  'deepseek-r1-distill-llama-70b': { paramName: 'reasoning_format', allowedEfforts: ['low', 'high'] },
  'moonshotai/kimi-k2-instruct-0905': { paramName: 'reasoning_format', allowedEfforts: ['low', 'high'] },
};

function getReasoningSupport(provider, model) {
  const entry = MODEL_REASONING[model];
  if (!entry) return { supported: false, allowedEfforts: [], paramName: null };
  return {
    supported: true,
    allowedEfforts: entry.allowedEfforts,
    paramName: entry.paramName,
  };
}

// Build the provider-specific body fragment to merge into a chat-completions
// request. Returns {} when the model does not support reasoning or the user
// chose "off".
function buildReasoningRequestParams(provider, model, effort) {
  if (!effort) return {};
  const entry = MODEL_REASONING[model];
  if (!entry) return {};
  if (!entry.allowedEfforts.includes(effort)) return {};
  if (entry.paramName === 'reasoning_format') {
    return { reasoning_format: 'parsed' };
  }
  const wireEffort = entry.wireEffortMap?.[effort] ?? effort;
  return { reasoning_effort: wireEffort };
}

export {
  getReasoningSupport,
  buildReasoningRequestParams,
  MODEL_REASONING,
};
